import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { Link, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { MLPolicyTypeUtil } from 'ml-portals-util-react';
import { UserService } from 'gw-capability-gateway';
import { MLGatewayAccountBillingDetailsService } from 'gw-capability-gateway-billing';
import styles from '../ActivityReportsComponent/ActivityReportsComponent.module.scss';
import messages from './MLPaymentLogComponent.messages';
import metadata from './MLPaymentLogComponent.metadata.json5';
import MLPaymentLogAdditionalFilter from './MLPaymentLogAdditionalFilter';

const MLPaymentLogComponent = (props) => {
    const {
        rowLimit,
        isDashboard,
        isCompactView,
        history,
        convertLocalDateToDate,
        formatAmountString
    } = props;

    const { getPolicyTypeName } = MLPolicyTypeUtil();
    const translator = useTranslator();
    const { authHeader } = useAuthentication();

    const [isLoading, updateIsLoading] = useState(false);
    const [filteredJobs, updateFilteredJobs] = useState([]);
    const [resultsRowCount, updateResultsRowCount] = useState(0);
    const [filterData, updateFilterData] = useState({});

    const adjustFilterDataForBC = (filterData) => {
        const producerCodes = (filterData.selectedProducerCode) 
            ? [filterData.selectedProducerCode]
            : filterData.producerCodes_ML.map((producerCode) => {                
                return producerCode.code;
            });

        return {
            toDate: filterData?.toDate_ML,
            fromDate: filterData?.fromDate_ML,
            policyType: filterData?.policyType_ML,
            producerCodes,
            paymentMethod: filterData?.paymentMethod
        };
    };

    const loadPaymentsData = useCallback(async (filteringData) => {
        filteringData = adjustFilterDataForBC(filteringData);
        const response = isDashboard
            ? await MLGatewayAccountBillingDetailsService.searchPaymentsForProducer(filteringData, rowLimit, authHeader)
            : await MLGatewayAccountBillingDetailsService.searchPaymentsForProducer(filteringData, null, authHeader);;

        updateFilteredJobs(response);
        updateIsLoading(false);
        updateResultsRowCount(response.length);
    }, [authHeader, isDashboard, rowLimit]);

    useEffect(() => {
        updateIsLoading(true);
        UserService.getCurrentUser(authHeader).then((user) => {
            const selectedGroupId = _.get(user.affiliatedGroup_ML, 'publicID');
            if (selectedGroupId) {
                UserService.getProducerCodesForGroup(selectedGroupId, authHeader).then((selectedProducerCodes) => {
                    const filter = {
                        producerCodes_ML: [
                            ...selectedProducerCodes
                        ]
                    };
                    loadPaymentsData(filter);
                });
            }
        });
    }, [authHeader, loadPaymentsData]);

    const onFilter = () => {
        updateIsLoading(true);
        if (filterData.selectedProducerCode) {
            loadPaymentsData(filterData);
        } else {
            UserService.getCurrentUser(authHeader).then((user) => {
                const selectedGroupId = (filterData.groupPublicID_ML) ? filterData.groupPublicID_ML : _.get(user.affiliatedGroup_ML, 'publicID');
                if (selectedGroupId) {
                    UserService.getProducerCodesForGroup(selectedGroupId, authHeader).then((selectedProducerCodes) => {
                        filterData.producerCodes_ML = [
                            ...selectedProducerCodes
                        ]
                        loadPaymentsData(filterData);
                    });
                }
            });
        }
    };

    const onClearFilters = () => {
        // Need to keep groupPublicID when clearing filters sow e can retrieve producer codes again
        if(filterData.groupPublicID_ML) {
            updateFilterData({groupPublicID_ML: filterData.groupPublicID_ML});
        } else {
            updateFilterData({});
        }
    };

    const getMinDate = () => {
        const date = new Date();
        date.setDate(date.getDate() - 45)
        return date;
    };

    const renderTransactionDateCell = (item) => {
        return item.transactionDate ? <span>{convertLocalDateToDate(item.transactionDate)}</span> : '-';
    };

    const renderCustomerName = (item) => {
        const formatString = (str) => {
            return str
                .replace(/(\B)[^ ]*/g, (match) =>(match.toLowerCase()))
                .replace(/^[^ ]/g, (match)=>(match.toUpperCase()));
        }

        const getAccountName = (accountName) =>{
            const index = accountName.indexOf(',');
            if (index !== -1) { 
                accountName = accountName.substring(0, accountName.indexOf(','));
            }

            const splittedString = accountName.split(' ');
            let result = '';
            for (let i = splittedString.length - 1; i >= 1; i--) { 
                result += splittedString[i];
            }
            result = `${result}, ${splittedString[0]}`;
            return formatString(result);
        };

        return (
            <Link to={`/accounts/${item.accountNumber}/summary`} className={styles.linkCellValue}>
                {(getAccountName(item.customerName))}
            </Link>
        );
    };

    const getProductName = (productCode) => {
        return getPolicyTypeName(productCode);
    };

    const renderProductCell = (item) => {
        return getProductName(item.policyType);
    };

    const renderPolicyCell = (item) => {
        if(_.isUndefined(item.policyNumber)) {
            return '-'
        }
        return (
            <Link to={`/policies/${item.policyNumber}/summary`} className={styles.linkCellValue}>
                {item.policyNumber}
            </Link>
        )
    };

    const renderAmountCell = (item) => {
        if (!item.paymentAmount) {
            return '$-';
        }
        return formatAmountString(item.paymentAmount);
    };

    const getPaymentMethodDisplay = (item) => {
        switch (item.paymentMethod) {
            case 'ACH/EFT':
                return translator(messages.bankAccount);
            case 'Credit Card':
                return translator(messages.creditDebitCard);
            case undefined:
                return '-';
            default:
                return item.paymentMethod;
        }
    };

    const renderPaymentMethodCell = (item) => {
        if (item.paymentType && item.paymentType === 'Agency Sweep') {
            return translator(messages.agencySweep);
        }
        return getPaymentMethodDisplay(item);
    };

    const renderPaymentTypeCell = (item) => {
        if (item.paymentType && item.paymentType === 'Agency Sweep') {
            return getPaymentMethodDisplay(item);
        }
        return (item.paymentType) ? item.paymentType : '-';
    };

    const renderLast4DigitsCell = (item) => {
        return (item.last4Digits) ? `...${item.last4Digits}` : '-';
    };

    const onViewAllButtonClicked = () => {
        const reportView = 'payment-log';
        history.push('/activity-reports', {reportView : reportView} );
    };

    const overrideProps = {
        statusDesc: {
            visible: isLoading,
            content: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.loading)}</span>)
        },
        mlJobResultsFilterComponent: {
            visible: !isCompactView,
            filterData,
            onFilter,
            onClearFilters,
            resultsRowCount,
            updateFilterData,
            dateLabel: translator(messages.paymentDateRange),
            additionalFiltersComponent: MLPaymentLogAdditionalFilter,
            maxDate: new Date(),
            minDate: getMinDate()

        },
        paymentLogTable: {
            data: filteredJobs,
            visible: !isLoading,
            defaultPageSize: rowLimit,
            noDataText: (<span tabindex="0" role="alert" aria-live="polite">{translator(messages.noPaymentsFound)}</span>),
            showPagination: !isCompactView,
            tableLabel: translator(messages.recentPayments)
        },
        viewAllPaymentsButton: {
            visible: isDashboard,
            onClick: onViewAllButtonClicked,
            mlTagManagerLinkClick: "Dashboard_activityReport_viewAll_contingencies"
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            renderTransactionDateCell,
            renderCustomerName,
            renderProductCell,
            renderPolicyCell,
            renderAmountCell,
            renderPaymentMethodCell,
            renderPaymentTypeCell,
            renderLast4DigitsCell
        },
        resolveClassNameMap: styles
    };
    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers} />
    );
};

MLPaymentLogComponent.propTypes = {
    showAgentChanges: PropTypes.bool.isRequired,
    rowLimit: PropTypes.number.isRequired,
    isDashboard: PropTypes.bool.isRequired,
    isCompactView: PropTypes.bool.isRequired,
    getCustomerDisplayNameRevert: PropTypes.func.isRequired,
    history: PropTypes.shape({push: PropTypes.func}).isRequired,
    convertLocalDateToDate: PropTypes.func.isRequired
}

export default withRouter(MLPaymentLogComponent);
